import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = {
  title: "",
  highlight: "",
  text: "",
  badge: "",
  button_text: "Découvrir",
  button_link: "/boutique",
  secondary_text: "",
  secondary_link: "",
  image_url: "",
  image_mobile_url: "",
  video_url: "",
  layout: "standard",
  show_text: true,
  show_button: true,
  darken: false,
  image_link: "",
  products: [],
  mobile_title: "",
  mobile_text: "",
  order: 0,
  is_active: true,
};

function AuthPanelCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/auth-panel").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, bullets: String(form.bullets_text ?? (form.bullets || []).join("\n")).split("\n").map((b) => b.trim()).filter(Boolean) };
      delete payload.bullets_text;
      const { data } = await api.put("/admin/settings/auth-panel", payload);
      setForm(data);
      toast.success("Panneau enregistré");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <AdminCard title="Panneau des pages connexion / inscription" description="Image de fond, textes et visibilité">
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      </AdminCard>
    );
  }

  const bulletsText = form.bullets_text ?? (form.bullets || []).join("\n");

  return (
    <AdminCard
      title="Panneau des pages connexion / inscription"
      description="Image de fond, textes et visibilité du panneau latéral"
      data-testid="auth-panel-card"
    >
      <div className="space-y-4">
        <button
          type="button"
          data-testid="auth-panel-toggle"
          onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors duration-200 ${
            form.enabled ? "border-emerald-200 bg-emerald-50" : "border-line"
          }`}
        >
          <span>
            <span className={`block text-xs font-bold ${form.enabled ? "text-emerald-700" : "text-slate-600"}`}>
              {form.enabled ? "Panneau activé" : "Panneau désactivé"}
            </span>
            <span className="mt-0.5 block text-[11px] text-slate-500">
              {form.enabled
                ? "Le panneau latéral (logo, textes, image) s'affiche sur les pages connexion et inscription."
                : "Le panneau est masqué : le formulaire occupe toute la largeur de la page."}
            </span>
          </span>
          <span className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors duration-200 ${form.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${form.enabled ? "translate-x-4" : ""}`} />
          </span>
        </button>

        <ImageUpload
          value={form.image_path}
          onChange={(path) => setForm((f) => ({ ...f, image_path: path }))}
          label="Image de fond (couvre entièrement le panneau)"
          testId="auth-panel-image"
        />
        {form.image_path && (
          <button
            type="button"
            data-testid="auth-panel-image-remove"
            onClick={() => setForm((f) => ({ ...f, image_path: "" }))}
            className="text-xs font-bold text-rose-600 hover:underline"
          >
            Retirer l'image de fond
          </button>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Titre">
            <input data-testid="auth-panel-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
          </Field>
          <Field label="Partie en bleu du titre">
            <input data-testid="auth-panel-highlight-input" value={form.title_highlight} onChange={(e) => setForm((f) => ({ ...f, title_highlight: e.target.value }))} className={inputCls} />
          </Field>
        </div>

        <Field label="Sous-titre">
          <textarea data-testid="auth-panel-subtitle-input" rows={2} value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} className={textareaCls} />
        </Field>

        <Field label="Points de la liste (un par ligne)">
          <textarea
            data-testid="auth-panel-bullets-input"
            rows={4}
            value={bulletsText}
            onChange={(e) => setForm((f) => ({ ...f, bullets_text: e.target.value }))}
            className={textareaCls}
          />
        </Field>

        <div className="flex justify-end">
          <button
            type="button"
            data-testid="auth-panel-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-dark disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </AdminCard>
  );
}

function TrustCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/hero-trust").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/hero-trust", form);
      setForm(data);
      toast.success("Badges enregistrés");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const setItem = (index, key, value) =>
    setForm((f) => {
      const items = [...f.items];
      items[index] = { ...items[index], [key]: value };
      return { ...f, items };
    });

  if (!form) {
    return (
      <AdminCard title="Badges de confiance de la bannière" description="Livraison instantanée, clés officielles, support…">
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      </AdminCard>
    );
  }

  return (
    <AdminCard
      title="Badges de confiance de la bannière"
      description="La rangée « Livraison instantanée · Clés officielles · Support 24/7 · Garantie » affichée dans la bannière"
      data-testid="trust-card"
    >
      <div className="space-y-4">
        <button
          type="button"
          data-testid="trust-toggle"
          onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors duration-200 ${
            form.enabled ? "border-emerald-200 bg-emerald-50" : "border-line"
          }`}
        >
          <span>
            <span className={`block text-xs font-bold ${form.enabled ? "text-emerald-700" : "text-slate-600"}`}>
              {form.enabled ? "Badges activés" : "Badges désactivés"}
            </span>
            <span className="mt-0.5 block text-[11px] text-slate-500">
              {form.enabled ? "La rangée de badges s'affiche dans la bannière d'accueil." : "La rangée de badges est masquée de la bannière."}
            </span>
          </span>
          <span className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors duration-200 ${form.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${form.enabled ? "translate-x-4" : ""}`} />
          </span>
        </button>

        <div className="grid gap-3 sm:grid-cols-2" data-testid="trust-items-list">
          {form.items.map((item, index) => (
            <div key={index} className="rounded-xl border border-line p-3" data-testid={`trust-item-${index}`}>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`trust-title-${index}`}
                  value={item.title}
                  onChange={(e) => setItem(index, "title", e.target.value)}
                  placeholder="Titre (ex : Livraison)"
                  className={inputCls}
                />
                <button
                  type="button"
                  data-testid={`trust-remove-${index}`}
                  onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== index) }))}
                  title="Retirer"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line text-slate-400 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <input
                data-testid={`trust-subtitle-${index}`}
                value={item.subtitle}
                onChange={(e) => setItem(index, "subtitle", e.target.value)}
                placeholder="Sous-titre (ex : instantanée)"
                className={`${inputCls} mt-2`}
              />
            </div>
          ))}
        </div>

        {form.items.length < 6 && (
          <button
            type="button"
            data-testid="trust-add"
            onClick={() => setForm((f) => ({ ...f, items: [...f.items, { title: "", subtitle: "" }] }))}
            className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 transition-colors duration-150 hover:border-brand hover:text-brand"
          >
            <Plus className="h-3.5 w-3.5" /> Ajouter un badge
          </button>
        )}

        <div className="flex justify-end border-t border-line pt-4">
          <button
            type="button"
            data-testid="trust-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-brand-dark disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </AdminCard>
  );
}

export default function AdminSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/slides");
      setSlides(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key) => (event) => {
    const target = event.target;
    setForm((f) => ({ ...f, [key]: target.type === "checkbox" ? target.checked : target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      order: Number(form.order || 0),
      products: Array.isArray(form.products)
        ? form.products
        : String(form.products).split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/slides/${editing.id}`, payload);
      else await api.post("/admin/slides", payload);
      toast.success(editing ? "Slide mise à jour" : "Slide créée");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (slide) => {
    try {
      await api.put(`/admin/slides/${slide.id}`, { ...slide, is_active: !slide.is_active });
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const move = async (index, direction) => {
    const next = [...slides];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSlides(next);
    await api.post("/admin/slides/reorder", { ids: next.map((s) => s.id) });
    load();
  };

  const remove = async (slide) => {
    if (!window.confirm("Supprimer cette slide ?")) return;
    try {
      await api.delete(`/admin/slides/${slide.id}`);
      toast.success("Slide supprimée");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const activeCount = slides.filter((s) => s.is_active).length;

  const bulk = useBulkDelete("/admin/slides/bulk-delete", load, "slide(s)");

  const videoRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const uploadVideo = async (file) => {
    if (!file) return;
    setVideoUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video_url: data.url }));
      toast.success("Vidéo téléversée");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setVideoUploading(false);
    }
  };

  return (
    <div className="space-y-6">
    <AdminCard
      title="Gestion de la bannière"
      description={`${activeCount} slide(s) active(s) — entre 3 et 5 recommandées`}
      action={
        <button
          data-testid="admin-add-slide"
          onClick={() => {
            setEditing(null);
            setForm({ ...EMPTY, order: slides.length });
            setOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-dark"
        >
          <Plus className="h-3.5 w-3.5" /> Nouvelle slide
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <>
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
          <SelectCheckbox testId="bulk-all-slides"
            checked={slides.length > 0 && bulk.selected.length >= slides.length}
            onChange={() => bulk.toggleAll(slides.map((s) => s.id))} />
          Tout sélectionner
        </div>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-slides" />
        <div className="space-y-3" data-testid="admin-slides-list">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              data-testid={`admin-slide-${index}`}
              className="flex flex-wrap items-center gap-4 rounded-xl border border-line p-4"
            >
              <SelectCheckbox testId={`bulk-slide-${slide.id}`}
                checked={bulk.selected.includes(slide.id)} onChange={() => bulk.toggle(slide.id)} />
              <div className="hero-shell flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                {slide.image_url || slide.products?.[0] ? (
                  <img
                    src={fileUrl(slide.image_url || slide.products[0])}
                    alt=""
                    className="h-full w-full object-cover opacity-90"
                  />
                ) : (
                  <span className="text-[10px] font-bold text-white/60">Visuel CSS</span>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ink">
                  {slide.title} <span className="text-brand">{slide.highlight}</span>
                </p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{slide.text}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Pill tone={slide.is_active ? "green" : "slate"}>{slide.is_active ? "Active" : "Inactive"}</Pill>
                  {slide.badge && <Pill tone="rose">{slide.badge}</Pill>}
                  <span className="text-[11px] text-slate-400">Ordre {slide.order}</span>
                  <span className="text-[11px] text-slate-400">→ {slide.button_link}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  data-testid={`slide-up-${index}`}
                  onClick={() => move(index, -1)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`slide-down-${index}`}
                  onClick={() => move(index, 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`slide-toggle-${index}`}
                  onClick={() => toggleActive(slide)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                >
                  {slide.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </button>
                <button
                  data-testid={`slide-edit-${index}`}
                  onClick={() => {
                    setEditing(slide);
                    setForm({ ...EMPTY, ...slide });
                    setOpen(true);
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`slide-delete-${index}`}
                  onClick={() => remove(slide)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editing ? "Modifier la slide" : "Nouvelle slide"} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-slide-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titre">
              <input data-testid="slide-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
            </Field>
            <Field label="Mot mis en avant (bleu)">
              <input data-testid="slide-form-highlight" value={form.highlight} onChange={set("highlight")} className={inputCls} />
            </Field>
          </div>

          <Field label="Texte de description">
            <textarea data-testid="slide-form-text" rows={2} value={form.text} onChange={set("text")} className={textareaCls} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Badge (ex : OFFRE LIMITÉE)">
              <input data-testid="slide-form-badge" value={form.badge} onChange={set("badge")} className={inputCls} />
            </Field>
            <Field label="Texte du bouton">
              <input data-testid="slide-form-button" value={form.button_text} onChange={set("button_text")} className={inputCls} />
            </Field>
            <Field label="Lien du bouton">
              <input data-testid="slide-form-link" value={form.button_link} onChange={set("button_link")} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Bouton secondaire (texte)">
              <input value={form.secondary_text} onChange={set("secondary_text")} className={inputCls} />
            </Field>
            <Field label="Bouton secondaire (lien)">
              <input value={form.secondary_link} onChange={set("secondary_link")} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <ImageUpload
              value={form.image_url}
              onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
              label="Visuel desktop"
              testId="slide-desktop-image"
            />
            <ImageUpload
              value={form.image_mobile_url}
              onChange={(url) => setForm((f) => ({ ...f, image_mobile_url: url }))}
              label="Visuel mobile"
              testId="slide-mobile-image"
            />
          </div>

          <Field label="Mise en page de la slide" hint="« Plein écran » : l'image ou la vidéo couvre entièrement l'arrière-plan de la bannière">
            <select data-testid="slide-form-layout" value={form.layout || "standard"} onChange={set("layout")} className={inputCls}>
              <option value="standard">Standard — texte à gauche, visuel à droite (affichage actuel)</option>
              <option value="background">Plein écran — image ou vidéo en arrière-plan</option>
            </select>
          </Field>

          {form.layout === "background" && (
            <>
              <Field label="Vidéo d'arrière-plan (optionnel)" hint="MP4/WebM — prioritaire sur l'image si les deux sont définis. Max 80 Mo.">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="slide-form-video"
                    value={form.video_url || ""}
                    onChange={set("video_url")}
                    placeholder="https://... ou téléversez une vidéo"
                    className={inputCls}
                  />
                  <input
                    ref={videoRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    data-testid="slide-form-video-file"
                    onChange={(e) => uploadVideo(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    data-testid="slide-form-video-upload"
                    onClick={() => videoRef.current?.click()}
                    disabled={videoUploading}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand disabled:opacity-60"
                  >
                    {videoUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    {videoUploading ? "Téléversement…" : "Téléverser"}
                  </button>
                </div>
              </Field>
              <label className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3 text-sm font-semibold text-ink" data-testid="slide-form-show-text-wrap">
                <input
                  type="checkbox"
                  data-testid="slide-form-show-text"
                  checked={form.show_text !== false}
                  onChange={set("show_text")}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                />
                Afficher le titre et les textes par-dessus l'image
              </label>
              <label className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3 text-sm font-semibold text-ink" data-testid="slide-form-darken-wrap">
                <input
                  type="checkbox"
                  data-testid="slide-form-darken"
                  checked={!!form.darken}
                  onChange={set("darken")}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                />
                Assombrir l'image (voile sombre pour améliorer la lisibilité du texte)
              </label>
              <Field label="Lien de l'image (optionnel)" hint="Si renseigné, un clic sur l'image ouvre ce lien. Ex : /boutique ou https://…">
                <input data-testid="slide-form-image-link" value={form.image_link || ""} onChange={set("image_link")} placeholder="/boutique ou https://…" className={inputCls} />
              </Field>
            </>
          )}

          <Field label="Images produits (une URL par ligne)" hint="Affichées en boîtes flottantes sur la bannière">
            <textarea
              data-testid="slide-form-products"
              rows={4}
              value={Array.isArray(form.products) ? form.products.join("\n") : form.products}
              onChange={(e) => setForm((f) => ({ ...f, products: e.target.value.split("\n") }))}
              className={textareaCls}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titre mobile (court)">
              <input data-testid="slide-form-mobile-title" value={form.mobile_title} onChange={set("mobile_title")} className={inputCls} />
            </Field>
            <Field label="Texte mobile (court)">
              <input data-testid="slide-form-mobile-text" value={form.mobile_text} onChange={set("mobile_text")} className={inputCls} />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-6 rounded-xl border border-line bg-surface p-4">
            <Field label="Ordre d'affichage" className="w-32">
              <input data-testid="slide-form-order" type="number" value={form.order} onChange={set("order")} className={inputCls} />
            </Field>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input
                type="checkbox"
                data-testid="slide-form-show-button"
                checked={form.show_button !== false}
                onChange={set("show_button")}
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
              />
              Afficher le bouton « Découvrir »
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input
                type="checkbox"
                data-testid="slide-form-active"
                checked={!!form.is_active}
                onChange={set("is_active")}
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
              />
              Slide active
            </label>
          </div>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-ink">
              Annuler
            </button>
            <button
              type="submit"
              data-testid="slide-form-submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </form>
      </AdminModal>
    </AdminCard>

    <AuthPanelCard />

    <TrustCard />
    </div>
  );
}
